// Tape skin — low-level transport.
//
// This is the seam between the four action services and the backend. The
// services own the MOCK vs REAL branch (a one-line `USE_MOCK_TAPE` guard at the
// top of each); when real, they call into here. Logic is lifted from
// src/hooks/useJamiePullAgent.ts (SSE consumption + headers) and
// src/components/jamiePullAgent/JamiePullAgentMessage.tsx (get-hierarchy
// hydration), kept self-contained so Tape has no runtime dep on the agent UI.

import { printLog } from '../../constants/constants.ts';
import { TAPE_API_URL } from '../../config/tapeConfig.ts';
import { getPulseHeader } from '../pulseService.ts';
import { QuotaExceededError, parseQuotaExceededResponse } from '../../types/errors.ts';
import type { TapeCitation } from './tapeTypes.ts';

const PULL_ENTITLEMENT = 'jamie-pull';
const TAPE_ENTITLEMENT = 'tape';

/** Base URL for every Tape backend call. Kept separate from the app's main
 *  `API_URL` so Tape can target a different environment (alpha / staging)
 *  without disturbing the rest of the app. Trailing slash trimmed so
 *  callers can always concatenate paths starting with `/`. Do not import
 *  `API_URL` for Tape traffic — use this. */
const API_URL = TAPE_API_URL.replace(/\/+$/, '');

/** Tape rides the main-app session: it reads the same `auth_token` written by
 *  AuthService and dispatches a window event on 401 that TapeAccessGate
 *  listens for. No Tape-specific token, no sessionStorage. */
const getAuthToken = (): string | null => localStorage.getItem('auth_token');

const attachAuthHeader = (headers: Record<string, string> = {}): Record<string, string> => {
  const t = getAuthToken();
  return t ? { ...headers, Authorization: `Bearer ${t}` } : headers;
};

/** Event name TapeAccessGate listens for. Dispatched on any 401 from a
 *  /api/tape/* call so mid-session expiry kicks the user back to sign-in
 *  without a page reload. */
export const TAPE_UNAUTHORIZED_EVENT = 'tape:unauthorized';

export const delay = (ms: number): Promise<void> => new Promise(r => setTimeout(r, ms));

/** RFC-7807 problem body backend returns on 403 (not entitled). Exposed
 *  so TapeAccessGate's 403 branch can render `detail` + `requestAccessUrl`
 *  verbatim. Status / type are validated by the caller. */
export interface TapeProblem {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  requestAccessUrl?: string;
}

/** Error thrown by `tapeFetch` on a 403 carrying an RFC-7807 body. Caught
 *  by TapeAccessGate's entitlement probe so the not-entitled UX can render
 *  the server-supplied detail / request-access link. */
export class TapeNotEntitledError extends Error {
  problem: TapeProblem;
  constructor(problem: TapeProblem) {
    super(problem.detail || problem.title || 'Not entitled');
    this.name = 'TapeNotEntitledError';
    this.problem = problem;
  }
}

/**
 * Single fetch helper for every /api/tape/* call. Attaches the main-app
 * Bearer token, dispatches a `tape:unauthorized` window event on 401 (the
 * TapeAccessGate listens), surfaces 403 as a `TapeNotEntitledError`, and
 * converts 429 into the project-standard QuotaExceededError so existing
 * handling reuses.
 *
 * Pass `json` to send a JSON body; otherwise use `body` for raw streams.
 */
export async function tapeFetch<T = unknown>(
  path: string,
  init: Omit<RequestInit, 'body' | 'headers'> & { json?: unknown; body?: BodyInit | null; headers?: Record<string, string> } = {}
): Promise<T> {
  const { json, headers: extraHeaders, body, ...rest } = init;
  const headers: Record<string, string> = { ...(extraHeaders || {}) };
  if (json !== undefined) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${API_URL}${path}`, {
    ...rest,
    headers: attachAuthHeader(headers),
    body: json !== undefined ? JSON.stringify(json) : body,
  });
  if (res.status === 401) {
    window.dispatchEvent(new Event(TAPE_UNAUTHORIZED_EVENT));
    throw new Error('Unauthorized');
  }
  if (res.status === 403) {
    let problem: TapeProblem = { status: 403 };
    try { problem = { ...problem, ...(await res.json() as TapeProblem) }; } catch { /* non-JSON body */ }
    throw new TapeNotEntitledError(problem);
  }
  if (res.status === 429) {
    throw new QuotaExceededError(await parseQuotaExceededResponse(res, TAPE_ENTITLEMENT));
  }
  if (!res.ok) {
    throw new Error(`${path}: ${res.status} ${res.statusText}`);
  }
  // 204 No Content / empty body — callers that don't need a payload (e.g.
  // request-access POST) get `undefined` instead of a JSON-parse crash.
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

/** POST /api/tape/request-access — body carries the user's free-text intent
 *  ("what I'd use The Tape for"). Backend identifies the user via the
 *  attached auth token; client doesn't need to repeat email/identity in the
 *  body. Returns silently on success (200 / 204); throws on non-2xx. */
export async function requestTapeAccess(intent: string): Promise<void> {
  await tapeFetch('/api/tape/request-access', {
    method: 'POST',
    json: { intent },
  });
}

/** Result of the entitlement probe TapeAccessGate runs on mount and after a
 *  fresh sign-in. The probe rides the existing /api/tape/feed endpoint —
 *  cheap (60s TTL cache on the backend) and returns useful payload for the
 *  feed UI in Module 3, so the response isn't wasted. */
export type TapeProbeResult =
  | { state: 'entitled' }
  | { state: 'unauthenticated' }
  | { state: 'not-entitled'; problem: TapeProblem }
  | { state: 'error'; error: Error };

/** Hit GET /api/tape/feed and classify the response into the gate's three
 *  branches (plus a transient `error` state for network blips). Callers
 *  short-circuit to `unauthenticated` themselves when no auth_token is
 *  present, so this is only invoked once we have a token to probe with. */
export async function probeTapeEntitlement(): Promise<TapeProbeResult> {
  try {
    await tapeFetch('/api/tape/feed');
    return { state: 'entitled' };
  } catch (err) {
    if (err instanceof TapeNotEntitledError) {
      return { state: 'not-entitled', problem: err.problem };
    }
    if (err instanceof Error && err.message === 'Unauthorized') {
      return { state: 'unauthenticated' };
    }
    return { state: 'error', error: err instanceof Error ? err : new Error(String(err)) };
  }
}

/** Confidence tier returned by every `synthesize` response. Unified across
 *  all kinds. Backend's editorial judgment of how well-supported the result
 *  is by the candidate pool. See
 *  docs/tape-backend-confidence-and-cadence-memo.md. */
export type TapeConfidence = 'strong' | 'partial' | 'thin' | 'empty';

/** Uniform _meta block the backend attaches to most /api/tape/* responses. */
export interface TapeResponseMeta {
  cached?: boolean;
  revalidated?: boolean;
  tier?: 'quantitative' | 'qualitative';
  fetchedAt?: string;
  cachedAt?: string;
  ageSec?: number;
  freshUntil?: string;
  stale?: boolean;
  staleReason?: string;
  source?: string;
  forced?: boolean;
  /** Synthesizer's confidence judgment. Absent on canon-only / non-synthesize
   *  responses. See TapeConfidence for tiers + client pill rendering. */
  confidence?: TapeConfidence;
  /** Short (≤100 char) explanation of WHY the confidence is what it is.
   *  Rendered verbatim in the pill popover and/or empty-state message. */
  confidenceReason?: string;
  /** Exact count of candidates the synthesizer worked over. */
  candidateCount?: number;
  /** Brief-only: actual lookback (in days) the backend used after auto-
   *  expanding. 7 = default, 30/90 = widened. */
  windowDays?: number;
  /** Brief-only: true when backend widened past the requested 7-day window. */
  windowExpanded?: boolean;
  /** True when web search results were blended into the citation arrays
   *  (because the podcast corpus was thin / stale / bear-starved on this
   *  query). Surfaces an "Includes web sources" note in the result header.
   *  Confidence may also be downgraded — web is less vetted than the
   *  corpus. See the web-citations backend memo. */
  webAugmented?: boolean;
}

/** Normalized response from GET /api/tape/quote/:slug (Yahoo / Finnhub proxy). */
export interface LiveTapeQuote {
  symbol: string;
  name?: string;
  price: number;
  currency?: string;
  dayChangePct: number;
  spark: number[];
  marketState?: string;
  _meta?: TapeResponseMeta;
}

/** Ticker price + sparkline for one symbol. Slug values match Yahoo (e.g. `APP`,
 *  `%5ETNX`, `DX-Y.NYB`, `CL%3DF`). */
export async function tickerQuote(slug: string): Promise<LiveTapeQuote> {
  return tapeFetch<LiveTapeQuote>(`/api/tape/quote/${slug.replace(/^\//, '')}`);
}

// ─── Composite endpoints — person-quotes / topic-quotes / synthesize ──────────
// The backend's candidate shape is a near-superset of TapeCitation: the same
// playable fields plus `spanSec`, `confidenceTier`, `_signals`. We extend
// TapeCitation rather than redefine — the extras are optional and pass through
// any UI that doesn't yet care about them.

export interface TapeCandidate extends TapeCitation {
  spanSec?: number;
  confidenceTier?: 'high' | 'medium' | 'low';
  _signals?: { dedicated?: boolean; mainstream?: boolean; spanRank?: number };
}

export interface PersonQuotesFilters {
  guestsOnly?: boolean;
  dedicatedOnly?: boolean;
  mainstream?: boolean;
  minSpan?: number;
  maxSpan?: number;
  minDate?: string | null;
  maxDate?: string | null;
  episodesLimit?: number;
  quotesPerEpisode?: number;
  candidatesLimit?: number;
  /** Pass `true` to suppress the per-kind recency weighting (Narrative uses
   *  this — the time dimension IS the point). See
   *  docs/tape-backend-recency-weighting.md. */
  disableRecencyWeighting?: boolean;
}

export interface PersonQuotesRequest {
  name: string;
  themes?: string[];
  filters?: PersonQuotesFilters;
  refresh?: boolean;
  /** Calling kind. Same purpose as on TopicQuotesRequest — lets backend
   *  pick the right window/policy per kind. When set, the client should
   *  not pin `filters.minDate`. */
  kind?: SynthesizeKind;
}

export interface PersonAppearance {
  guid: string;
  title: string;
  feedTitle: string;
  publishedDate?: string;
  role?: 'guest' | 'host';
  imageUrl?: string;
}

export interface PersonQuotesResponse {
  person: string;
  appearances: PersonAppearance[];
  candidates: TapeCandidate[];
  _meta?: TapeResponseMeta & { underlying?: Record<string, number> };
}

export async function personQuotes(req: PersonQuotesRequest): Promise<PersonQuotesResponse> {
  return tapeFetch<PersonQuotesResponse>('/api/tape/person-quotes', { method: 'POST', json: req });
}

export interface TopicQuotesFilters {
  mainstream?: boolean;
  minDate?: string | null;
  maxDate?: string | null;
  feedIds?: string[];
  minSpan?: number;
  candidatesLimit?: number;
  /** Pass `true` to suppress the per-kind recency weighting (Narrative uses
   *  this — the time dimension IS the point). See
   *  docs/tape-backend-recency-weighting.md. */
  disableRecencyWeighting?: boolean;
}

export interface TopicQuotesRequest {
  query?: string;
  themes?: string[];
  filters?: TopicQuotesFilters;
  groupBy?: 'creator' | 'bull-bear' | null;
  refresh?: boolean;
  /** Calling kind. Lets backend pick the right time window / cadence /
   *  auto-expand policy per kind (Brief: 7d → 30d → 90d auto-expand,
   *  Narrative: wide multi-year window, etc.). When set, the client should
   *  NOT pin `filters.minDate` — backend owns window selection. */
  kind?: SynthesizeKind;
}

export interface TopicQuotesGroup {
  key: string;
  candidates: TapeCandidate[];
}

export interface TopicQuotesResponse {
  query: string;
  candidates: TapeCandidate[];
  groups?: TopicQuotesGroup[];
  _meta?: TapeResponseMeta & { underlying?: Record<string, number> };
}

export async function topicQuotes(req: TopicQuotesRequest): Promise<TopicQuotesResponse> {
  return tapeFetch<TopicQuotesResponse>('/api/tape/topic-quotes', { method: 'POST', json: req });
}

export type SynthesizeKind = 'dossier' | 'brief' | 'split' | 'narrative' | 'readin';

export interface SynthesizeRequest {
  kind: SynthesizeKind;
  input: {
    person?: string | null;
    personB?: string | null;
    topic?: string | null;
    ticker?: string | null;
    depth?: 'quick' | 'brief' | 'deep';
    /** Narrative group filter — `bulls` / `bears` / `<named person>` /
     *  empty-or-null = consensus. Sent so the backend's narrative prompt
     *  can frame the synthesis around the right population. */
    group?: string | null;
  };
  candidates: TapeCandidate[];
  model?: 'fast' | 'quality';
  stream?: false;
  refresh?: boolean;
}

export interface SynthesizeResponse {
  kind: SynthesizeKind;
  text: string;
  /** Backend-curated relevant tickers, ordered by relevance (4-8 typical).
   *  For readin, these are PEERS (not the queried ticker). For all other kinds
   *  they're the names a finance pro would put on the tape for this story.
   *  Empty array when nothing confident — render an empty strip (i.e. hide). */
  tickers: string[];
  tokens?: { input: number; output: number };
  model?: string;
  elapsedMs?: number;
  _meta?: TapeResponseMeta;
}

export async function synthesize(req: SynthesizeRequest): Promise<SynthesizeResponse> {
  if (typeof console !== 'undefined' && process.env.NODE_ENV !== 'production') {
    // Dev token-usage logger; tightened to one line per call after the response.
  }
  const res = await tapeFetch<SynthesizeResponse>('/api/tape/synthesize', { method: 'POST', json: { stream: false, ...req } });
  if (typeof console !== 'undefined' && process.env.NODE_ENV !== 'production' && res.tokens) {
    // eslint-disable-next-line no-console
    console.info(`[tape synth/${req.kind}] in=${res.tokens.input} out=${res.tokens.output} model=${res.model || '?'} ms=${res.elapsedMs || '?'}`);
  }
  return res;
}

/**
 * Map `{{clip:id}}` tokens found inside `text` back to their TapeCitation
 * forms, using the candidate pool returned by person-quotes / topic-quotes.
 * Preserves first-seen order; skips ids not present in the pool.
 */
export function resolveCitationsFromCandidates(text: string, candidates: TapeCandidate[]): TapeCitation[] {
  const byId = new Map<string, TapeCandidate>();
  for (const c of candidates) byId.set(c.pineconeId, c);
  const seen = new Set<string>();
  const out: TapeCitation[] = [];
  for (const id of extractCitationIds(text)) {
    if (seen.has(id)) continue;
    seen.add(id);
    const c = byId.get(id);
    if (c) out.push(c);
  }
  return out;
}

/** Citation token convention shared with the agent UI.
 *  Tolerant to both `{{clip:id}}` (canonical) and `{{clip=id}}` (equals-sign
 *  variant some synths emit). Captured ids are trimmed. */
const CLIP_TOKEN_RE = /\{\{clip\s*[:=]\s*([^}]+)\}\}/g;
export const extractCitationIds = (text: string): string[] => {
  const ids: string[] = [];
  for (const m of text.matchAll(CLIP_TOKEN_RE)) ids.push(m[1].trim());
  return [...new Set(ids)];
};
export const stripCitationTokens = (text: string): string => text.replace(CLIP_TOKEN_RE, '').trim();

/**
 * Run a single /api/pull turn and return the full synthesized text.
 * Tape only needs the final text (citations are embedded as tokens); tool_result
 * events carry only counts, so they're ignored. Throws QuotaExceededError on 429.
 */
export async function runPull(
  message: string,
  opts: { model?: 'quality' | 'fast'; signal?: AbortSignal } = {}
): Promise<string> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    ...getPulseHeader(),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_URL}/api/pull`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ message, model: opts.model || 'quality', stream: true }),
    signal: opts.signal,
  });

  if (res.status === 429) {
    throw new QuotaExceededError(await parseQuotaExceededResponse(res, PULL_ENTITLEMENT));
  }
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  if (!res.body) throw new Error('Response body is null');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let eventType = 'message';
  let text = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7).trim();
        continue;
      }
      if (!line.startsWith('data: ')) continue;
      const raw = line.slice(6).trim();
      if (!raw || raw === '[DONE]') continue;
      try {
        const payload = JSON.parse(raw);
        if (eventType === 'text_delta') text += payload.text || '';
        else if (eventType === 'text' || eventType === 'text_done') {
          if (payload.text) text = payload.text;
        } else if (eventType === 'error') {
          throw new Error(payload.error || 'Agent error');
        }
      } catch (e) {
        printLog(`[tape] failed to parse SSE data: ${raw}`);
      }
      eventType = 'message';
    }
  }
  return text;
}

/** Hydrate a single citation id into a TapeCitation via get-hierarchy. */
export async function getHierarchy(pineconeId: string, speaker?: string): Promise<TapeCitation | null> {
  try {
    const res = await fetch(`${API_URL}/api/get-hierarchy?paragraphId=${encodeURIComponent(pineconeId)}`);
    if (!res.ok) return null;
    const data = await res.json();
    const h = data.hierarchy;
    const para = h?.paragraph?.metadata;
    const ep = h?.episode?.metadata;
    return {
      pineconeId,
      text: para?.text || '',
      speaker,
      episodeTitle: ep?.title || para?.episode || 'Unknown episode',
      creator: ep?.creator || para?.creator || '',
      episodeImage: ep?.imageUrl || para?.episodeImage || '',
      audioUrl: para?.audioUrl || ep?.audioUrl || '',
      startTime: para?.start_time ?? 0,
      endTime: para?.end_time ?? 0,
      publishedDate: ep?.publishedDate || para?.publishedDate || undefined,
    };
  } catch {
    return null;
  }
}

/** Hydrate every citation token found in `text`, preserving first-seen order. */
export async function hydrateCitations(text: string): Promise<TapeCitation[]> {
  const ids = extractCitationIds(text);
  const settled = await Promise.all(ids.map(id => getHierarchy(id)));
  return settled.filter((c): c is TapeCitation => c !== null);
}

/** The one genuinely-new backend endpoint: deterministic weekly mention counts. */
export async function fetchTimeline(params: {
  topic: string;
  startDate: string;
  endDate: string;
}): Promise<{ topic: string; startDate: string; endDate: string; buckets: { weekStart: string; count: number }[]; totalMentions: number }> {
  const qs = new URLSearchParams({
    topic: params.topic,
    startDate: params.startDate,
    endDate: params.endDate,
    interval: 'week',
  });
  const res = await fetch(`${API_URL}/api/tape/timeline?${qs.toString()}`, {
    headers: { ...getPulseHeader() },
  });
  if (res.status === 429) {
    throw new QuotaExceededError(await parseQuotaExceededResponse(res, PULL_ENTITLEMENT));
  }
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}
